/**
 * useBays Hook
 * Fetches and manages workshop bays for job assignments
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { BayType } from '../services/statusTransitionService';

export interface Bay {
  id: string;
  name: string;
  bayType: BayType;
  maxConcurrentJobs: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface BaysResponse {
  success: boolean;
  data: Bay[];
}

/**
 * Fetch active bays from the API
 */
async function fetchBays(): Promise<Bay[]> {
  const response = await api.get<BaysResponse>('/api/admin/bays');
  if (!response.success) {
    throw new Error('Failed to fetch bays');
  }
  return response.data;
}

/**
 * Hook to get all active bays
 */
export function useBays() {
  return useQuery({
    queryKey: ['bays'],
    queryFn: fetchBays,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to get bays filtered by type
 */
export function useBaysByType(bayType?: BayType) {
  const { data: bays, ...rest } = useBays();

  const filteredBays = bayType
    ? bays?.filter((bay) => bay.bayType === bayType)
    : bays;

  return {
    ...rest,
    data: filteredBays,
  };
}
